/**
 * The "Rejection report": the auditor's claim decisions for a period, as an
 * Excel workbook.
 *
 * Shared by the auditor dashboard and the Reports page's Rejections tab so the
 * two downloads stay identical. Both feed it the same shape - the dashboard
 * from /auditor/claims/audit-outcomes, Reports from /reports/audit, which the
 * server builds with the same computeAuditOutcomes:
 *   totals{decisions,approved,returned,returnRate}, byRegion[], decisionsList[],
 *   reportRejections{count,claimsAffected,list[]}, directorFlags{count,list[]}.
 *
 * Sheets: a summary, the per-region rates, every individual decision with its
 * reason - the reasons are the part that makes a rate actionable rather than
 * just a number - then report rejections and director flags when there are any.
 */
import ExcelJS from 'exceljs';
import { saveBlob } from './download.mjs';

const RED = { argb: 'FF8C1515' };

const header = (ws, labels) => {
  const row = ws.addRow(labels);
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: RED };
  return row;
};

const widths = (ws, list) => list.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

const shortId = (id) => (id ? String(id).slice(-8).toUpperCase() : '-');

/** How the outcome reads in a cell and on screen. */
export const outcomeLabel = (d) => (d.outcome === 'returned' ? 'Returned' : 'Approved');
export const claimTypeLabel = (t) => (t === 'PerDiemClaim' ? 'Per Diem' : 'Expense');
export const rateLabel = (r) => (r === null || r === undefined ? 'n/a' : `${r}%`);

/**
 * @param {object} data     the audit-outcomes payload
 * @param {object} opts
 * @param {string} opts.region       'all' or a region name, for the title line
 * @param {string} opts.periodLabel  e.g. "FY26 (Sep 2025 - Aug 2026)"
 * @param {Date}   opts.from, opts.to the inclusive window the decisions cover
 * @returns {ExcelJS.Workbook}
 */
export const buildAuditOutcomesWorkbook = (data, { region = 'all', periodLabel, from, to }) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Stanford Seed Expense Claim System';

  const summary = wb.addWorksheet('Summary');
  summary.addRow(['CLAIM AUDIT OUTCOMES']).font = { bold: true, size: 14, color: RED };
  summary.addRow([
    `${!region || region === 'all' ? 'All regions' : region}` +
    ` · ${periodLabel}` +
    ` · generated ${new Date().toLocaleString('en-US')}`,
  ]);
  if (from && to) {
    summary.addRow([
      `Decisions counted from ${from.toLocaleDateString('en-US')} to ${to.toLocaleDateString('en-US')} inclusive.`,
    ]).font = { italic: true, size: 9 };
  }
  summary.addRow([]);
  summary.addRow(['CLAIM-LEVEL DECISIONS']).font = { bold: true };
  summary.addRow(['Decisions recorded', data.totals.decisions]);
  summary.addRow(['Approved', data.totals.approved]);
  summary.addRow(['Returned for revision', data.totals.returned]);
  summary.addRow([
    '  - includes claims flagged during report review',
  ]).font = { italic: true, size: 9 };
  summary.addRow(['Return rate', rateLabel(data.totals.returnRate)]);
  summary.addRow([]);
  // Kept separate from the rate above: a rejected report bounces every claim
  // it holds, so counting those per claim would blame six claims for one
  // claim's problem.
  summary.addRow(['REPORT-LEVEL REJECTIONS']).font = { bold: true };
  summary.addRow(['Reports rejected', data.reportRejections?.count ?? 0]);
  summary.addRow(['Claims caught up in them', data.reportRejections?.claimsAffected ?? 0]);
  summary.addRow([]);
  summary.addRow([
    'Note: report rejections are counted once per report, not once per claim, '
    + 'so they are excluded from the claim-level return rate above.',
  ]);
  summary.getColumn(1).width = 30;
  summary.getColumn(2).width = 18;

  const byRegion = wb.addWorksheet('By region');
  header(byRegion, ['Region', 'Approved', 'Returned', 'Return rate']);
  for (const r of data.byRegion || []) {
    byRegion.addRow([r.region, r.approved, r.returned, rateLabel(r.returnRate)]);
  }
  widths(byRegion, [24, 12, 12, 14]);

  const detail = wb.addWorksheet('Decisions');
  // 'How' distinguishes the two ways a claim is sent back - flagged while
  // reviewing a report, or returned straight to the admin - because they
  // mean different things to whoever reads this.
  header(detail, [
    'Date', 'Outcome', 'How', 'Claim type', 'Claimant', 'Region', 'Auditor',
    'Reason / note', 'Claim #', 'Report #',
  ]);
  for (const d of data.decisionsList || []) {
    detail.addRow([
      d.performedAt ? new Date(d.performedAt).toLocaleString('en-US') : '',
      outcomeLabel(d),
      d.via || '',
      claimTypeLabel(d.claimType),
      d.userName, d.region || '-', d.auditor,
      d.comments || '',
      String(d.claimId).slice(-8).toUpperCase(),
      shortId(d.reportId),
    ]);
  }
  widths(detail, [22, 12, 18, 12, 24, 16, 22, 52, 12, 12]);

  if ((data.reportRejections?.count ?? 0) > 0) {
    const rr = wb.addWorksheet('Report rejections');
    header(rr, [
      'Rejected', 'Claims in report', 'Claims flagged', 'Which claims',
      'Regions', 'Reason', 'Report #',
    ]);
    for (const r of data.reportRejections.list || []) {
      const flagged = r.flaggedClaims || [];
      rr.addRow([
        r.rejectedAt ? new Date(r.rejectedAt).toLocaleString('en-US') : '',
        r.claimsAffected,
        // The number that were actually the problem. This is the column that
        // answers "the whole report was rejected, but was it 1 of 6?".
        flagged.length,
        flagged.length
          ? flagged.map(f => `${f.claimId} (${f.claimant})`).join(', ')
          : 'none flagged individually',
        (r.regions || []).join(', '),
        r.comments || '',
        shortId(r.reportId),
      ]);
    }
    widths(rr, [22, 16, 14, 44, 24, 52, 12]);
  }

  // Claim-level flags raised by the directors, kept out of the auditor's rate
  // but reported - a director flagging claims the auditor passed is the
  // single most useful thing in here.
  if ((data.directorFlags?.count ?? 0) > 0) {
    const df = wb.addWorksheet('Director flags');
    header(df, ['Date', 'Flagged by', 'Role', 'Claim type', 'Claimant', 'Region', 'Reason', 'Claim #']);
    for (const f of data.directorFlags.list || []) {
      df.addRow([
        f.performedAt ? new Date(f.performedAt).toLocaleString('en-US') : '',
        f.flaggedBy,
        (f.role || '').replace(/_/g, ' '),
        claimTypeLabel(f.claimType),
        f.userName, f.region || '-',
        f.reason || '',
        String(f.claimId).slice(-8).toUpperCase(),
      ]);
    }
    widths(df, [22, 22, 18, 12, 24, 16, 52, 12]);
  }

  return wb;
};

/** Build the workbook and save it as Claim_Audit_Outcomes_<period>.xlsx. */
export const downloadAuditOutcomesExcel = async (data, opts) => {
  const wb = buildAuditOutcomesWorkbook(data, opts);
  const buffer = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Claim_Audit_Outcomes_${String(opts.periodLabel || '').replace(/[^\w]+/g, '_')}.xlsx`
  );
};

export default downloadAuditOutcomesExcel;
